import { accountDisplayName } from './account-display';

/**
 * Document-store layout, per association (issue #373).
 *
 *   Condos/ARLINGTON COMMONS/
 *   ├── Owner-Files/
 *   │   └── 8580OF - Ponder/      ← FLAT: every document for this owner, no subfolders
 *   │       ├── 2026.09.16 - Exterior Improvement Approval - Shed.pdf
 *   │       └── 2024-notice.pdf
 *   ├── Financial-Banking/ … Meetings/ … Insurance/ …
 *
 * The unit folder uses the app's own account label (`accountDisplayName`, e.g.
 * "8580OF - Ponder") so the drive matches what staff see everywhere else — the earlier
 * "Unit-N" sketch was a placeholder.
 *
 * ## Why a unit folder is FLAT
 *
 * It used to be seeded with seven typed subfolders (`Correspondence/`, `Forms/`,
 * `Violations/`, …) that the account page rendered as tabs. An owner has a handful of
 * documents in total, so that split imposed a filing decision on every upload and a hunt
 * across seven tabs on every read, and bought nothing: the default view was already the
 * flattened "everything for this person" list. The date+type is the FILE name, which is
 * how staff name things on the drive anyway.
 *
 * Nothing was migrated on the drive — folders that already exist keep their files, and
 * the recursive listing in `api/documents/accounts.ts` still surfaces them. So a unit
 * folder may legitimately CONTAIN subfolders; we simply never create or require one.
 *
 * Every segment is sanitised and guaranteed non-empty. A blank segment would
 * silently collapse the tree and scatter records under the wrong parent — much
 * harder to notice, and to undo, than an obviously-named "Unmatched" folder.
 *
 * **These are BUSINESS rules, not storage-provider rules.** The taxonomy outlived the
 * previous provider unchanged; only the module name and the export prefixes moved.
 */

/**
 * A folder name safe for the document store.
 *
 * Re-verified against DROPBOX, which is stricter than Box was:
 *
 * - `/` and `\` are path separators (both providers). Replaced with " - " rather than
 *   dropped so "Storm / Screen Doors" stays readable instead of running together.
 * - Control characters are rejected outright. `\p{Cc}` rather than an explicit range so
 *   the C1 block is covered too — those arrive from pasted Windows-1252 text.
 * - A name may not END in a space or a period; Dropbox refuses the create rather than
 *   trimming for you. The `trim()` plus the leading/trailing-dot strip covers both, and
 *   the SECOND `trim()` is what catches "Foo . ", where stripping the dot re-exposes a
 *   trailing space.
 * - 255 characters is the per-component ceiling.
 *
 * Deliberately NOT filtered: `< > : " | ? *` and the reserved Windows device names
 * (`CON`, `PRN`, `NUL`, `COM1`…). Those are DESKTOP-SYNC restrictions, not API
 * restrictions — the Dropbox API accepts them, and folding them out would rename folders
 * that already exist on the drive. A rename is exactly how a community's document history
 * gets orphaned (#373), so the safer failure here is the unusual character surviving.
 */
export function safeFolderName(name?: string | null, fallback = 'Unnamed'): string {
	const cleaned = (name ?? '')
		.replace(/[\\/]+/g, ' - ')
		.replace(/\p{Cc}/gu, '')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/^\.+|\.+$/g, '')
		.trim();
	// A name that survives sanitising with no letters or digits left ("///" becomes
	// "-") is not a usable folder name — fall back rather than create it.
	if (!/[a-z0-9]/i.test(cleaned)) return fallback;
	return cleaned.length > 255 ? cleaned.slice(0, 255) : cleaned;
}

/**
 * The comparison form of a folder name — lowercase, and nothing else.
 *
 * **Every comparison between a name we DERIVED and a name the DRIVE reported must go
 * through this.** Dropbox paths are case-insensitive but case-PRESERVING: `path_display`
 * keeps whatever casing a human typed, so `Condos/WESTMINSTER VILLAGE CONDO` and the
 * `Condos/Westminster Village Condo` we derive from `associations.name` are the same
 * folder as far as the storage layer is concerned. A byte-wise test says they are not,
 * and since the derived path IS the folder's identity (see `associationBucket`), that
 * verdict removes a whole community from the app: absent from its bucket listing,
 * un-openable because the download guard re-derives from `path_display`, and un-indexed
 * because the delta walk skips a file with no visible association. SBS names folders in
 * caps by hand and the Box mirror delivered them that way, so this is the common case,
 * not an edge one.
 *
 * `toLowerCase()` deliberately, and NOT a looser equivalence — it is the same operation
 * Dropbox itself applies to produce `path_lower`. Folding accents or punctuation would
 * make two genuinely different folders compare equal, and the tiebreak that resolves
 * that is invisible.
 */
export function folderKeyOf(name: string): string {
	return name.toLowerCase();
}

/**
 * The drive's id for the folder called `name`, out of a listing, or null.
 *
 * Lives here because it is another instance of the ONE rule this module owns: a name we hold
 * (from a URL segment, from `associations.name`) and a name the DRIVE reported are compared
 * through `folderKeyOf` and never byte-wise. SBS names folders in caps by hand, so a byte-wise
 * lookup finds nothing for exactly the folders that matter.
 *
 * Null when the listing does not contain it — a stale or not-yet-loaded column is the normal
 * case, and the callers treat a missing id as "not recorded" rather than an error.
 */
export function findFolderIdByName<T extends { id: string; name: string }>(
	folders: readonly T[] | undefined,
	name: string | undefined,
): string | null {
	if (!folders || !name) return null;
	const wanted = folderKeyOf(name);
	return folders.find((folder) => folderKeyOf(folder.name) === wanted)?.id ?? null;
}

/**
 * Every top-level bucket an association can be filed into — the SINGLE source of truth,
 * shared by browsing, search facets and the archive sweep.
 *
 * Those consumers have to iterate these to find association folders. When one kept its own
 * hardcoded copy, a bucket added here would never be scanned: filing would happily put
 * associations somewhere the tooling is blind to. Keep this and `typeFolder` in the same
 * file so they cannot drift.
 */
export const DOCUMENT_TYPE_FOLDERS = ['Condos', 'HOAs', 'Other', 'Archived'] as const;


export type AssociationBucket = typeof DOCUMENT_TYPE_FOLDERS[number];

/**
 * Where a RETIRED community's folder lives.
 *
 * Deliberately a peer of `Condos`/`HOAs` rather than a subfolder of either, and
 * deliberately FLAT inside — `Archived/<Association>`, with no condo/HOA split. The
 * document browser, the search facets and the archive sweep all read the taxonomy
 * positionally as `<bucket>/<association>/<category>/…`; an extra level under `Archived`
 * would shift association names into the category slot for archived communities only,
 * which is the kind of asymmetry that goes unnoticed until a report is silently wrong.
 */
export const ARCHIVED_FOLDER: AssociationBucket = 'Archived';

/**
 * Root folders that are browsable but are NOT association buckets.
 *
 * The distinction is load-bearing, not cosmetic. Under a bucket, depth 1 is a COMMUNITY, and
 * the browser checks it against the association roster — a folder with no matching row is
 * hidden, because it is either a stale rename or a community this user may not see. These
 * hold ordinary filing (company-wide paperwork, the scan inbox), so depth 1 is just a folder
 * and applying that check would hide every one of them.
 *
 * They are also deliberately absent from DOCUMENT_TYPE_FOLDERS: `typeFolder` must never
 * derive them, or provisioning would try to file an association into the scan inbox.
 */
export const LIBRARY_FOLDERS = ['General', 'Inbox (Scanned Documents)'] as const;

export type LibraryFolder = typeof LIBRARY_FOLDERS[number];

/**
 * Every folder the ROOT column may show, in the order it should read.
 *
 * Buckets first (the day-to-day work), then the shared libraries, then `Archived` last —
 * retired communities are reference material, not somewhere anyone is filing today. The UI
 * separates that final group with a rule; see `rootFolderGroup`.
 */
export const ROOT_BROWSABLE_FOLDERS = [
	'Condos', 'HOAs', 'Other', ...LIBRARY_FOLDERS, ARCHIVED_FOLDER,
] as const;

/**
 * Where the automated off-platform backups are filed.
 *
 * A peer of `Condos`/`HOAs`/`Archived` in the team root, and DELIBERATELY absent from both
 * `DOCUMENT_TYPE_FOLDERS` and `ROOT_BROWSABLE_FOLDERS`. That absence is the whole isolation
 * mechanism, and both lists are allow-lists, so it costs nothing to maintain:
 *
 *  - `planEntry` (api/documents/index-sync.ts) skips any entry whose first segment is not in
 *    DOCUMENT_TYPE_FOLDERS, so the nightly delta walk never indexes a backup artifact. Adding
 *    `Backups` there would file every part-zip into `documents` and surface gigabytes of
 *    archives in document search.
 *  - The root browse guard and filter (api/dropbox/documents.ts) test ROOT_BROWSABLE_FOLDERS,
 *    so the folder stays out of the document browser — backups are an admin concern, reached
 *    through the admin Backup page, not something to click into next to a community's files.
 *
 * Nothing derives an association bucket from this value; it is only ever used to build the
 * upload path in api/cron/backup-archive.ts.
 */
export const BACKUPS_FOLDER = 'Backups';

/**
 * Comparison key → the CANONICAL spelling, for every root folder the browser may show.
 *
 * The root vocabulary needs the same case-insensitivity as association folders, and for
 * the same reason — the drive is free to hold `CONDOS`. It needs one thing more, though:
 * a canonical form to map back TO. A drive folder's own spelling is what a listing should
 * display, but it is NOT what may be stored or compared downstream — `documents.bucket`
 * feeds an indexed filter (`documents(association_id, bucket)`), so writing `CONDOS`
 * there would silently exclude those rows from every query that looks for `Condos`.
 * Resolve through here and store what comes back.
 */
const ROOT_FOLDER_BY_KEY = new Map<string, string>(
	ROOT_BROWSABLE_FOLDERS.map((name) => [folderKeyOf(name), name]),
);

/** The canonical spelling of a root folder, or null when it is not one of ours. */
export function canonicalRootFolder(name: string): string | null {
	return ROOT_FOLDER_BY_KEY.get(folderKeyOf(name)) ?? null;
}

const BUCKET_BY_KEY = new Map<string, AssociationBucket>(
	DOCUMENT_TYPE_FOLDERS.map((name) => [folderKeyOf(name), name]),
);

/**
 * The canonical bucket a folder name denotes, or null when it denotes none.
 *
 * The gate for anything that accepts a bucket from OUTSIDE — a drive listing, a URL
 * segment, an operator-supplied path. Returning the canonical value rather than a boolean
 * is what keeps the caller from carrying the caller's spelling forward.
 */
export function canonicalBucket(name: string): AssociationBucket | null {
	return BUCKET_BY_KEY.get(folderKeyOf(name)) ?? null;
}

/**
 * The leading number of an SBS-numbered folder (`1-CONDOMINIUMS` → 1), or null.
 *
 * SBS hand-numbers the roots they work from every day — `1-CONDOMINIUMS` through
 * `14- Nyree` — so the prefix IS their priority ordering, expressed in the only place a
 * file server lets you express one. The browser reproduces it rather than imposing its own.
 *
 * ## Why one-or-two digits, not `\d+`
 *
 * The drive also holds `2023-2024 Before snow pictures for Winnscapes`, which a greedy
 * `^(\d+)\s*-` matches as folder number 2023 — hoisting a photo dump above every real
 * bucket. SBS's numbering runs 1–14, so capping the prefix at two digits separates a
 * priority number from a year without needing to know the range.
 *
 * Spacing around the dash varies on the real drive (`1-CONDOMINIUMS`, `11- ZEGO`,
 * `13- CONTRACTOR LIST`), so it is optional on both sides.
 *
 * Deliberately does NOT match `-JOB SEARCH & TRAINING`: a bare leading dash sorts it first
 * on their file server, but it carries no number and the rule here is numbering.
 */
export function numberedFolderPrefix(name: string): number | null {
	const match = name.trim().match(/^(\d{1,2})\s*-/);
	return match ? Number(match[1]) : null;
}

export type RootFolderGroup = 'bucket' | 'library' | 'archived';

/** Which group a root folder belongs to, so the client can order and rule them off. */
export function rootFolderGroup(name: string): RootFolderGroup {
	const key = folderKeyOf(name);
	if (key === folderKeyOf(ARCHIVED_FOLDER)) return 'archived';
	return LIBRARY_FOLDERS.some((lib) => folderKeyOf(lib) === key) ? 'library' : 'bucket';
}

/** True when depth 1 under `name` is a COMMUNITY and must be checked against the roster. */
export function isAssociationBucket(name: string): boolean {
	return canonicalBucket(name) !== null;
}

/**
 * Top-level bucket from `associations.type` (stored as 'condo' / 'HOA').
 * An unrecognised type goes to a visible "Other" rather than being filed under
 * the wrong bucket, where nobody would think to look for it.
 *
 * Knows nothing about archive state — see `associationBucket`, which is what callers
 * holding a whole association row should use. Keeping this one a pure type→bucket map
 * means adding the archive rule could not change the meaning of any existing call site.
 */
export function typeFolder(type?: string | null): AssociationBucket {
	switch ((type ?? '').trim().toLowerCase()) {
		case 'condo': return 'Condos';
		case 'hoa': return 'HOAs';
		default: return 'Other';
	}
}

export interface AssociationBucketFields {
	type?: string | null;
	/** Epoch ms, as `associations.archived_at` stores it. */
	archivedAt?: number | null;
	/** `associations.status` — 'active' | 'inactive' | 'archived'. */
	status?: string | null;
}

/**
 * The bucket an association's folder belongs in RIGHT NOW — archive state wins over type.
 *
 * Both signals are checked because the app writes both: `components/associations/
 * archive-button.tsx` sends `{ status: 'archived', archivedAt: Date.now() }` in one PUT,
 * but they are independent columns and older rows carry only one. A stricter test here
 * would leave half-archived rows filed as live.
 *
 * **The derived path IS the identity of the folder.** Under the id-addressed store that was
 * not true: a recorded folder id was the key, and this function only ever decided where that
 * folder was moved TO. Dropbox is path-addressed and those columns are gone (migration 240),
 * so this plus
 * `safeFolderName(name)` is now the WHOLE answer to "which folder is this association's".
 *
 * Read the consequence carefully, because it inverts the old invariant: a bucket change
 * (archive, un-archive, retype) or a rename RELOCATES the folder by definition. Nothing
 * looks the old folder up by id any more, so `api/documents/archive.ts` must move it on
 * the drive to match — otherwise the next lookup lands on a path that does not exist and
 * the community's documents are, for every practical purpose, gone.
 */
export function associationBucket(association: AssociationBucketFields): AssociationBucket {
	if (association.archivedAt != null) return ARCHIVED_FOLDER;
	if ((association.status ?? '').trim().toLowerCase() === 'archived') return ARCHIVED_FOLDER;
	return typeFolder(association.type);
}

export interface AssociationDocumentPathFields extends AssociationBucketFields {
	name?: string | null;
	/**
	 * `associations.dropbox_path` — an explicit `<Bucket>/<Folder Name>` OVERRIDE, or
	 * null/absent to derive from the name (the normal case).
	 *
	 * Set by a super admin when the drive folder is named something the derivation cannot
	 * reach: a short name kept from a takeover, a pre-rename name, a community filed under
	 * the wrong bucket. Case differences need no pin — those are folded already.
	 */
	dropboxPath?: string | null;
}

/**
 * The `[bucket, folderName]` an association's documents live under — the SINGLE answer.
 *
 * Everything that reads or writes an association's folder goes through here: the browser,
 * the download guard, the delta indexer, uploads, provisioning, the owner-index manifest
 * and the archive sweep. That is not tidiness — a caller that derived its own path while
 * this one honoured a pin would read one folder and write to another, and both operations
 * would look successful.
 *
 * A pin wins outright when it parses. Anything else falls back to the derivation rather
 * than throwing: a malformed value is rejected at the API boundary where a human can be
 * told about it, and a stored row that somehow got past that must not take the community's
 * documents offline.
 *
 * Returns `string[]`, not `[string, string]` — a community may sit at any depth under a
 * root (see `parseAssociationFolderPin`). The DERIVED case is still exactly two, so a caller
 * that wants the folder's own name must take the LAST segment, never `[1]`.
 *
 * Must go through `associationBucket`, never `typeFolder`: an archived association's
 * folder lives under `Archived`, and a path built from its type alone lands on nothing.
 */
export function associationFolderSegments(
	association: AssociationDocumentPathFields,
): string[] {
	const pinned = parseAssociationFolderPin(association.dropboxPath);
	if (pinned) return pinned;
	return [
		associationBucket(association),
		safeFolderName(association.name, 'Unknown Association'),
	];
}

/**
 * Read a stored or submitted pin, or null when it is not a usable one.
 *
 * Exported because the API boundary needs the SAME parse it will later be resolved with —
 * validating with one rule and resolving with another is how a value gets accepted and
 * then silently ignored. The folder half goes through `safeFolderName` so a pin cannot
 * introduce a path segment the app would never have created itself.
 */
export function parseAssociationFolderPin(
	raw?: string | null,
): string[] | null {
	const segments = (raw ?? '').split('/').map((s) => s.trim()).filter(Boolean);
	/**
	 * AT LEAST two segments, at ANY depth. Previously exactly two.
	 *
	 * The first segment is NOT checked against `canonicalBucket`. That check was right while
	 * the app owned the tree — the only legal roots were the four it created. The tree is now
	 * an rclone mirror of SBS's file server, whose roots are `1-CONDOMINIUMS`, `3-HOA's`,
	 * `7-CLOSED (PAST) ACCOUNTS` and ~86 others, none of which `canonicalBucket` knows.
	 * Keeping it meant **every real pin was rejected**, so no drive folder could be linked to
	 * its community at all.
	 *
	 * ## Why the depth-2 ceiling is gone (issue #526)
	 *
	 * `7-CLOSED (PAST) ACCOUNTS/5-MASTER ASSOCIATIONS/COLONY HILL REC ASSOC(s)` is a real
	 * community at depth 3, and under the old rule it could not be pinned AT ALL — the parse
	 * refused it, so it fell back to a derived `Condos/<name>` path that does not exist on
	 * this drive.
	 *
	 * Relaxing the parse alone would have been WORSE than the limitation: the two-segment
	 * shape used to be baked into `browseKey(bucket, folderName)`, `assertPathVisible`'s
	 * depth-1 check and `planEntry`'s `visible.get(browseKey(folders[0], folders[1]))`, so a
	 * deeper pin would have produced a key nothing ever looked up. Those three now do
	 * LONGEST-PREFIX attribution over the whole segment list (`associationsForPath` in
	 * api/documents/associations.ts), which is what makes a deep pin resolve rather than
	 * silently vanish. Do not re-relax one without the other.
	 *
	 * ## Why the floor is two, not one
	 *
	 * A one-segment pin would nominate a ROOT as a community folder. Depth 0 is the root
	 * column, `isAssociationRoot` asks whether communities are filed BENEATH a root, and the
	 * browser lists roots rather than treating one as a community — so a one-segment pin
	 * would contradict the level it landed on rather than being merely unusual. "Under the
	 * root" is the rule, and that means at least one segment below it.
	 *
	 * What still constrains the value: every segment non-empty and put through
	 * `safeFolderName`, so a pin cannot introduce `..`, a path separator, a control character
	 * or a segment the app would never have created itself. That is the property the API gate
	 * depends on — it validates with this exact function before storing.
	 *
	 * What this function deliberately CANNOT check is whether a segment is hidden: it is pure
	 * and the `hidden` flag lives in `document_folder_settings`. `api/associations.ts` applies
	 * that check at the boundary, where it can read D1.
	 */
	if (segments.length < 2) return null;
	const cleaned = segments.map((segment) => safeFolderName(segment, ''));
	if (cleaned.some((segment) => !segment)) return null;
	return cleaned;
}

/** The `<bucket>/<folder name>` path for ONE association, as a string. */
export function associationDocumentPath(association: AssociationDocumentPathFields): string {
	return associationFolderSegments(association).join('/');
}

export interface DocumentFolderPathFields {
	associationName?: string | null;
	associationType?: string | null;
	accountId?: string | null;
	accountNumber?: string | null;
	accountOwnerName?: string | null;
	accountAddress?: string | null;
	requestTypeName?: string | null;
	createdAt?: string | number | null;
}

/**
 * Catch-all unit folder for records that could not be tied to an account.
 *
 * Exported because it is NOT a unit: anything asking "is this a real owner folder"
 * has to exclude it, and a second copy of the literal would drift.
 */
export const UNMATCHED_ACCOUNT_FOLDER = 'Unmatched';

/**
 * The category folder that holds one subfolder per unit.
 *
 * Exported for the same reason as `UNMATCHED_ACCOUNT_FOLDER`: anything that has to
 * recognise "this path is an owner path" before it seeds a unit folder would otherwise
 * keep a second copy of the literal, which would drift from the one `documentFolderPath`
 * builds.
 */
export const OWNER_FILES_FOLDER = 'Owner-Files';

/** The account's folder name — the shared label convention, or "Unmatched". */
function accountFolder(app: DocumentFolderPathFields): string {
	if (!app.accountId) return UNMATCHED_ACCOUNT_FOLDER;
	return safeFolderName(
		accountDisplayName({
			accountNumber: app.accountNumber ?? null,
			primaryOwnerName: app.accountOwnerName ?? null,
			primaryOwnerCompanyName: app.accountOwnerName ? null : (app.accountAddress ?? null),
		}),
		UNMATCHED_ACCOUNT_FOLDER,
	);
}

/**
 * Ordered folder segments, root first. Create/lookup each in turn under its parent.
 *
 * Ends at the UNIT folder — there is no leaf below it any more. Callers that want the
 * unit's own name take the LAST segment, which every current one already does.
 */
export function documentFolderPath(app: DocumentFolderPathFields): string[] {
	return [
		typeFolder(app.associationType),
		safeFolderName(app.associationName, 'Unknown Association'),
		OWNER_FILES_FOLDER,
		accountFolder(app),
	];
}
